import React, { ChangeEventHandler, MouseEventHandler, ReactElement, forwardRef } from 'react';
import styled from 'styled-components';

import { ColorTypes } from '@folx-types/theme';

import theme from '@theme/FolxTheme';

interface IRadioWithRefProps {
	name?: string;
	value?: string;
	checkedColor?: ColorTypes;
	className?: string;
	disabled?: boolean;
	onClick?: MouseEventHandler;
	onChange?: ChangeEventHandler;
	stacked?: boolean;
	checked?: boolean;
	variant?: 'default' | 'small';
}

type Ref = HTMLInputElement;

const RadioWithRef = forwardRef<Ref, IRadioWithRefProps>(
	({ className, name, value, onClick, onChange, disabled = false, ...rest }, ref) => (
		<input
			type="radio"
			className={className}
			name={name}
			value={value}
			onClick={onClick}
			onChange={onChange}
			disabled={disabled}
			{...rest}
			ref={ref}
		/>
	)
);
RadioWithRef.displayName = 'RadioWithRef';

const StyledRadio = styled(RadioWithRef)<{ variant: 'default' | 'small' }>`
	border: 0;
	clip: rect(0 0 0 0);
	height: 1px;
	margin: -1px;
	overflow: hidden;
	padding: 0;
	position: absolute;
	width: 1px;

	& + span {
		display: flex;
		flex-flow: ${({ stacked }) => (stacked ? 'column' : 'row')};
		align-items: ${({ stacked }) => (stacked ? 'flex-start' : 'center')};
	}

	& + span:before {
		background: ${theme.colors.white};
		border: 0.2rem solid ${theme.colors.gray};
		border-radius: 50%;
		box-sizing: border-box;
		content: '';
		display: inline-flex;
		height: ${({ theme: components, variant }) => components.radio[variant].outerSize};
		margin-right: ${theme.spacing.xs};
		margin-bottom: ${({ stacked }) => (stacked ? '2.4rem' : '0')};
		min-width: ${({ theme: components, variant }) => components.radio[variant].outerSize};
		max-width: ${({ theme: components, variant }) =>
			components.radio[variant].outerSize}; /* To prevent shrinkage */
		position: relative;
		transition: 0.5s ease background;
		vertical-align: -0.25em;
	}

	& + span:after {
		background: ${theme.colors.white};
		border-radius: 50%;
		content: '';
		height: ${({ theme: components, variant }) => components.radio[variant].innerSize};
		position: absolute;
		min-width: ${({ theme: components, variant }) => components.radio[variant].innerSize};
		max-width: ${({ theme: components, variant }) => components.radio[variant].innerSize};
		transition: 0.5s ease background;
		margin: 0.4rem;
	}

	&:checked + span:before {
		border: 0.2rem solid
			${({ theme: components, variant, disabled }) =>
				disabled
					? components.radio[variant].disabledColor
					: components.radio[variant].checkedColor};
	}

	&:checked + span:after {
		background: ${({ theme: components, variant, disabled }) =>
			disabled ? components.radio[variant].disabledColor : components.radio[variant].checkedColor};
	}

	&:focus-visible + span:before {
		outline: 0.2rem solid ${theme.colors.darkGray};
	}

	&:checked:focus-visible + span:before {
		outline: 0.2rem solid -webkit-focus-ring-color;
		outline: 0.2rem solid -moz-focus-ring-color;
	}

	@media (hover: hover) {
		&:hover + span:before,
		&:hover + span:after {
			background: ${({ theme: components, variant, disabled }) =>
				!disabled && components.radio[variant].hoverColor};
		}

		&:checked:hover + span:before {
			border: ${({ theme: components, variant, disabled }) =>
				!disabled && `0.4rem solid ${components.radio[variant].checkedColor}`};
		}

		&:checked:hover + span:after {
			background: ${({ disabled }) => !disabled && theme.colors.white};
		}
	}
`;

export default StyledRadio;
